import {
  type ChangeEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { MUTATIONS, useMutation } from "../../../api";
import type {
  MyCommentReaction,
  ShowroomComment,
  ShowroomCommentId,
  UnseenComment,
} from "../../../api/comment";
import type { ShowroomEntryId } from "../../../api/showroom";
import type { SmallTeamId } from "../../../api/smallTeam";
import type { SparkId } from "../../../api/spark";
import type { MeResponse, ProfileImageData } from "../../../api/user";
import type { WorkshopId, WorkshopUserRole } from "../../../api/workshop";
import useToggle from "../../../hooks/useToggle.js";
import HumanTime from "../../../lib/HumanTime.jsx";
import Avatar from "../../../design-system/components/Avatar/Avatar";
import cx from "../../../lib/cx.js";
import { linkify } from "../../../lib/linkify";
import { preLine } from "../../../lib/text.css";
import { hasRole, Roles } from "../../../lib/Roles";
import { pluralize } from "../../../lib/text-utils.js";
import useIsInView from "../../../hooks/useIsInView.js";
import ReactButton, {
  reactions,
  type Reaction,
} from "../../../design-system/primitives/components/ReactButton/ReactButton";
import { useConfirmAction } from "../../../design-system/primitives/components/AlertDialog/useConfirmAction";
import { Bubble } from "../../../design-system/components/Bubble/Bubble.jsx";
import TextInput from "../../../design-system/primitives/components/TextInput/TextInput";
import Text from "../../../design-system/primitives/components/Text/Text";
import TextareaPrimitive from "../../../design-system/primitives/components/Textarea/Textarea";
import IconButton from "../../../design-system/primitives/components/IconButton/IconButton";
import ButtonPrimitive from "../../../design-system/primitives/components/Button/Button";
import styles from "./Comments.css";

// Authenticated users post as themselves; anonymous ones pass the captcha
// `token` and a display `name` together.
type NewComment = {
  content: string;
  parentCommentId?: ShowroomCommentId;
} & ({ token: string; name: string } | { token?: never; name?: never });

// Returns nothing while a previous submission is still in flight.
type CreateComment = (comment: NewComment) => Promise<unknown> | undefined;

type CreateFormProps = {
  onCreateComment: CreateComment;
  parentCommentId?: ShowroomCommentId;
  label?: string;
};

const UserCreateForm = ({
  onCreateComment,
  parentCommentId,
  label = "Dein Kommentar",
  me,
}: CreateFormProps & { me: NonNullable<MeResponse> }) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!content.trim() || submitting) return;

    const pending = onCreateComment({ content, parentCommentId });
    if (!pending) return;
    setSubmitting(true);
    void pending
      .then(() => {
        setContent("");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  return (
    <form className={styles.commentInput} onSubmit={handleSubmit}>
      <Avatar user={me} size={"30px"} />
      <div className={styles.commentInputField}>
        <TextareaPrimitive
          name="content"
          value={content}
          onChange={onChange}
          placeholder={label}
          aria-label={label}
          disabled={submitting}
          variant="filled"
          autoGrow
        />
      </div>
      <IconButton
        type="submit"
        icon="send-horizontal"
        ariaLabel="Absenden"
        color="accent"
        disabled={submitting || !content.trim()}
      />
    </form>
  );
};

type HCaptcha = {
  render: (
    node: HTMLElement,
    options: {
      sitekey: string;
      theme: "dark" | "light";
      callback: (token: string) => void;
      "expired-callback": () => void;
      "error-callback": (err: unknown) => void;
    },
  ) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    hcaptcha?: HCaptcha;
    hcaptchaOnLoad?: () => void;
  }
}

const onLoadListeners: (() => void)[] = [];
let apiScriptRequested = false;
let captchaReady = false;

const mountCaptchaScript = () => {
  if (apiScriptRequested) return;
  apiScriptRequested = true;
  window.hcaptchaOnLoad = () => {
    onLoadListeners.forEach((fn) => fn());
    captchaReady = true;
  };

  const script = document.createElement("script");
  script.src = `https://js.hcaptcha.com/1/api.js?render=explicit&onload=hcaptchaOnLoad`;
  script.async = true;
  document.head.appendChild(script);
};

const useCaptcha = ({ onVerify }: { onVerify: (token: string) => void }) => {
  const [ready, setReady] = useState(captchaReady);
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const cbRef = useRef({ onVerify });
  useEffect(() => {
    cbRef.current = { onVerify };
  });

  useEffect(() => {
    if (node) mountCaptchaScript();
  }, [node]);
  useEffect(() => {
    if (!ready) {
      onLoadListeners.push(() => setReady(true));
    } else if (node) {
      const { hcaptcha } = window;
      if (!hcaptcha) {
        console.warn("couldn't find `window.hcaptcha`");
        return undefined;
      }
      const id = hcaptcha.render(node, {
        sitekey: import.meta.env.VITE_HCAPTCHA_SITEKEY,
        theme: "dark",
        callback: (response) => cbRef.current.onVerify(response),
        "expired-callback": () => console.warn("hcaptcha expired"),
        "error-callback": (err) => {
          console.error("hcaptcha error", err);
          hcaptcha.reset(id);
        },
      });
      return () => hcaptcha.remove(id);
    }
    return undefined;
  }, [node, ready]);

  return setNode;
};

const AnonCreateForm = ({
  onCreateComment,
  parentCommentId,
  label = "Dein Kommentar",
}: CreateFormProps) => {
  const [showCaptcha, { on }] = useToggle();
  const [values, setValues] = useState({ content: "", name: "" });

  const handleVerify = (token: string) =>
    void onCreateComment({ token, parentCommentId, ...values });

  const ref = useCaptcha({ onVerify: handleVerify });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!values.content.trim() || !values.name.trim()) return;
    on();
  };

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
  };

  return showCaptcha ? (
    <div ref={ref} />
  ) : (
    <form className={styles.formContainerColumn} onSubmit={handleSubmit}>
      <TextInput
        name="name"
        value={values.name}
        onChange={onChange}
        label="Dein Name"
      />
      <TextareaPrimitive
        name="content"
        value={values.content}
        onChange={onChange}
        label={label}
        variant="filled"
        autoGrow
      />
      <ButtonPrimitive
        type="submit"
        title="Absenden"
        color="accent"
        iconRight="send-horizontal"
      />
    </form>
  );
};

const CreateCommentForm = ({
  me,
  allowAnonUsers,
  ...rest
}: CreateFormProps & { me: MeResponse; allowAnonUsers?: boolean }) => {
  if (me) return <UserCreateForm me={me} {...rest} />;
  if (allowAnonUsers) return <AnonCreateForm {...rest} />;
  return null;
};

type EditCommentFormProps = {
  comment: DisplayComment;
  onClose: () => void;
  smallTeamId: SmallTeamId;
};

const EditCommentForm = ({
  comment,
  onClose,
  smallTeamId,
}: EditCommentFormProps) => {
  const [doUpdate] = useMutation(MUTATIONS.updateComment);
  const [content, setContent] = useState(comment.content);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!content.trim() || submitting) return;

    const pending = doUpdate({ content, commentId: comment.id, smallTeamId });
    if (!pending) return;
    setSubmitting(true);
    void pending.then(onClose).finally(() => {
      setSubmitting(false);
    });
  };

  const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  return (
    <form className={styles.commentInput} onSubmit={handleSubmit}>
      <div className={styles.commentInputField}>
        <TextareaPrimitive
          name="content"
          value={content}
          onChange={onChange}
          placeholder="Dein Kommentar"
          aria-label="Dein Kommentar"
          variant="filled-raised"
          autoGrow
        />
      </div>
      <IconButton
        type="submit"
        icon="send-horizontal"
        ariaLabel="Ändern"
        color="accent"
        disabled={submitting || !content.trim()}
      />
    </form>
  );
};

const reactionKeys = Object.keys(reactions) as Reaction[];

type OnSeen = (commentId: ShowroomCommentId) => void;

const UnseenBadge = ({
  onSeen,
  commentId,
}: {
  onSeen: OnSeen;
  commentId: ShowroomCommentId;
}) => {
  const [visible, setVisible] = useState(false);
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (visible && !triggered) {
      const timeoutId = setTimeout(() => {
        onSeen(commentId);
        setTriggered(true);
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [triggered, visible, onSeen, commentId]);

  const ref = useIsInView({
    onVisible: () => setVisible(true),
    onInvisible: () => setVisible(false),
  });
  return (
    <span ref={ref}>
      <Bubble text={"Neu!"} topRight={false} />
    </span>
  );
};

type CommentFooterProps = {
  comment: DisplayComment;
  showEdit: boolean;
  toggleEdit?: () => void;
  showReplyForm: boolean;
  toggleReply?: () => void;
  smallTeamId: SmallTeamId;
  canDelete: boolean;
};

const CommentFooter = ({
  comment,
  showEdit,
  toggleEdit,
  showReplyForm,
  toggleReply,
  smallTeamId,
  canDelete,
}: CommentFooterProps) => {
  const [doAdd] = useMutation(MUTATIONS.addCommentReaction);
  const [doRemove] = useMutation(MUTATIONS.removeCommentReaction);
  const [doDelete] = useMutation(MUTATIONS.deleteComment);
  const confirmDelete = useConfirmAction({
    onConfirm: () => doDelete({ commentId: comment.id, smallTeamId }),
    title: "Kommentar löschen?",
    description: "Der Kommentar wird für alle entfernt.",
    errorDescription:
      "Der Kommentar konnte nicht gelöscht werden. Bitte versuche es noch einmal.",
    variant: "destructive",
    actionLabel: "Löschen",
    actionIcon: "trash",
  });

  if (comment.deletedAt) return null;

  return (
    <div className={styles.footer}>
      {reactionKeys.map((r) => {
        const isActive = comment.myReaction === r;
        const input = { reaction: r, commentId: comment.id, smallTeamId };
        return (
          <ReactButton
            key={r}
            reaction={r}
            selected={isActive}
            count={comment.reactionCounts[r] || 0}
            onClick={(e) => {
              e.stopPropagation();
              return isActive ? doRemove(input) : doAdd(input);
            }}
          />
        );
      })}
      {toggleReply && (
        <ButtonPrimitive
          title={showReplyForm ? "Abbrechen" : "Antworten"}
          size="small"
          variant="secondary"
          onClick={toggleReply}
        />
      )}
      {toggleEdit && (
        <IconButton
          icon="pencil"
          ariaLabel={showEdit ? "Bearbeiten abbrechen" : "Kommentar bearbeiten"}
          aria-pressed={showEdit}
          variant="tertiary"
          onClick={toggleEdit}
        />
      )}
      {canDelete && (
        <>
          <IconButton
            icon="trash"
            ariaLabel="Kommentar löschen"
            variant="tertiary"
            onClick={confirmDelete.open}
          />
          {confirmDelete.dialogElement}
        </>
      )}
    </div>
  );
};

type CommentHeaderProps = {
  comment: DisplayComment;
  isUnseen: boolean;
  onSeen: OnSeen;
};

const CommentHeader = ({ comment, isUnseen, onSeen }: CommentHeaderProps) => {
  const [hasBeenUnseen, setHasBeenUnseen] = useState(isUnseen);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isUnseen) setHasBeenUnseen(true);
  }, [isUnseen]);

  if (comment.deletedAt)
    return (
      <div className={styles.headerMeta}>
        gelöscht <HumanTime date={new Date(comment.deletedAt)} />
      </div>
    );

  return (
    <div className={styles.header}>
      <Text variant="label-large">{comment.author.name ?? ""}</Text>
      {hasBeenUnseen && <UnseenBadge onSeen={onSeen} commentId={comment.id} />}
      <span className={styles.headerMeta}>
        <HumanTime date={comment.createdAt} />
        {comment.lastEditedAt && <> | editiert</>}
      </span>
    </div>
  );
};

// A comment as shown: its author filled in with a placeholder when the
// original one was deleted, and the viewer's own reaction attached.
type DisplayAuthor = {
  id: string;
  name: string | null;
  profileImageData?: ProfileImageData | null;
};
type DisplayComment = Omit<ShowroomComment, "author"> & {
  author: DisplayAuthor;
  myReaction: string | null;
};

const unknownAuthor: DisplayAuthor = {
  id: "",
  name: "Unbekannt",
};

type CommentProps = {
  comment: DisplayComment;
  me: MeResponse;
  smallTeamId: SmallTeamId;
  childComments: DisplayComment[];
  onCreateComment?: CreateComment;
  unseenIds: Set<ShowroomCommentId>;
  onSeen: OnSeen;
  isPublic?: boolean;
  isAdmin: boolean;
};

const Comment = ({
  comment,
  me,
  smallTeamId,
  childComments,
  onCreateComment,
  unseenIds,
  onSeen,
  isPublic,
  isAdmin,
}: CommentProps) => {
  const canEdit = comment.author.id === me?.id;
  const [showAllReplies, showAllRepliesActions] = useToggle();
  const [showEdit, showEditActions] = useToggle();
  const [showReplyForm, showReplyFormActions] = useToggle();

  const shownChildren =
    showAllReplies || childComments.length < 2
      ? childComments
      : childComments.slice(0, 1);
  const nonShownCount = childComments.length - shownChildren.length;

  const handleCreateComment =
    onCreateComment &&
    ((data: NewComment) =>
      onCreateComment(data)?.then(() => {
        showReplyFormActions.off();
        showAllRepliesActions.on();
      }));

  const realShowReplyForm =
    handleCreateComment && (showReplyForm || childComments.length > 0);

  const hasNew = childComments.slice(1).some((c) => unseenIds.has(c.id));

  return (
    <div className={styles.commentsContainer}>
      <div className={styles.card}>
        <div className={styles.mainRow}>
          <Avatar user={comment.author} size={"24px"} />
          <div className={styles.contentColumn}>
            <CommentHeader
              comment={comment}
              isUnseen={unseenIds.has(comment.id)}
              onSeen={onSeen}
            />
            {!comment.deletedAt &&
              (showEdit ? (
                <EditCommentForm
                  comment={comment}
                  onClose={showEditActions.off}
                  smallTeamId={smallTeamId}
                />
              ) : (
                <p className={cx(styles.body, preLine)}>
                  {linkify(comment.content)}
                </p>
              ))}
          </div>
        </div>
        <CommentFooter
          comment={comment}
          showEdit={showEdit}
          toggleEdit={canEdit ? showEditActions.toggle : undefined}
          showReplyForm={showReplyForm}
          toggleReply={
            childComments.length === 0 && me && onCreateComment
              ? showReplyFormActions.toggle
              : undefined
          }
          smallTeamId={smallTeamId}
          canDelete={isAdmin}
        />
      </div>

      {(childComments.length > 0 || realShowReplyForm) && (
        <div className={styles.threadContainer}>
          {shownChildren.length > 0 && (
            <div className={styles.commentsContainer}>
              {shownChildren.map((child) => (
                <Comment
                  key={child.id}
                  comment={child}
                  me={me}
                  smallTeamId={smallTeamId}
                  childComments={[]}
                  unseenIds={unseenIds}
                  onSeen={onSeen}
                  isPublic={isPublic}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          )}
          {nonShownCount > 0 && (
            <ButtonPrimitive
              size="small"
              variant="secondary"
              title={`${pluralize(
                nonShownCount,
                `weitere${hasNew ? " neue " : " "}Antwort`,
                `weitere${hasNew ? " neue " : " "}Antworten`,
              )} sehen`}
              onClick={showAllRepliesActions.on}
            />
          )}
          {realShowReplyForm && (
            <CreateCommentForm
              onCreateComment={handleCreateComment}
              parentCommentId={comment.id}
              me={me}
              label="Deine Antwort"
              allowAnonUsers={isPublic}
            />
          )}
        </div>
      )}
    </div>
  );
};

type CommentsProps = {
  comments: ShowroomComment[];
  // Absent on the public showroom, which has no workshop context.
  workshopId?: WorkshopId | null;
  sparkId?: SparkId;
  me: MeResponse;
  entryId: ShowroomEntryId;
  smallTeamId: SmallTeamId;
  myRoles: readonly WorkshopUserRole[];
  myCommentReactions: MyCommentReaction[];
  unseenComments: UnseenComment[];
  isPublic?: boolean;
};

const Comments = ({
  comments,
  workshopId,
  sparkId,
  me,
  entryId,
  smallTeamId,
  myRoles,
  myCommentReactions,
  unseenComments,
  isPublic,
}: CommentsProps) => {
  const myReactions = new Map(
    myCommentReactions.map((r) => [r.commentId, r.reaction]),
  );
  const unseenIds = new Set(unseenComments.map((uc) => uc.commentId));
  const rootComments: DisplayComment[] = [];
  const childComments = new Map<ShowroomCommentId, DisplayComment[]>();
  comments.forEach((c) => {
    const nextC = {
      ...c,
      // Deleted users' comments come back without an author.
      author: c.author ?? unknownAuthor,
      myReaction: myReactions.get(c.id) ?? null,
    };
    if (c.parentCommentId) {
      const siblings = childComments.get(c.parentCommentId) ?? [];
      siblings.push(nextC);
      childComments.set(c.parentCommentId, siblings);
    } else {
      rootComments.push(nextC);
    }
  });

  const [doAddComment] = useMutation(MUTATIONS.addComment);
  const handleCreateComment = (comment: NewComment) =>
    doAddComment({ ...comment, entryId, smallTeamId, workshopId, sparkId });

  const [doSee] = useMutation(MUTATIONS.seenComment);
  const handleSeen = useCallback(
    (commentId: ShowroomCommentId) => {
      // Only workshop members have unseen comments to mark.
      if (workshopId) void doSee({ commentId, workshopId });
    },
    [workshopId, doSee],
  );
  const isAdmin = hasRole(myRoles, Roles.ADMIN);

  return (
    <div className={styles.commentsContainer}>
      <div className={styles.commentsContainer}>
        {comments.length === 0 ? (
          <p className={styles.noCommentsNote}>Noch keine Kommentare</p>
        ) : (
          rootComments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              me={me}
              smallTeamId={smallTeamId}
              childComments={childComments.get(comment.id) ?? []}
              onCreateComment={handleCreateComment}
              unseenIds={unseenIds}
              onSeen={handleSeen}
              isPublic={isPublic}
              isAdmin={isAdmin}
            />
          ))
        )}
      </div>
      <CreateCommentForm
        onCreateComment={handleCreateComment}
        me={me}
        allowAnonUsers={isPublic}
      />
    </div>
  );
};

export default Comments;
